import React, { useState, useEffect, useCallback } from 'react';
import { expeditionAPI } from '../services/api';
import { useLocation } from 'react-router-dom';
import { TrackingTimelineBlock } from '../components/TrackingTimelineBlock';

const initialAddress = {
  name: 'Non renseigné',
  phone: 'Non renseigné',
  email: 'Non renseigné',
  address: 'Non renseigné',
  company: ''
};

const STATUS_LABELS = {
  '1': 'Planifié',
  '2': 'En cours d’’affectation',
  '3': 'En chemin vers le lieu de ramassage',
  '4': 'Arrivé au lieu de ramassage',
  '5': 'En chemin vers la destination',
  '6': 'Arrivé à destination',
  '7': 'Retour au magasin',
  '8': 'Terminé',
  '9': 'Livraison annulée',
};

const normalizeStatusCode = (status) => {
  if (status === null || status === undefined) return '';
  const statusStr = String(status).trim();
  if (STATUS_LABELS[statusStr]) {
    return statusStr;
  }
  const entry = Object.entries(STATUS_LABELS).find(([, label]) => label.toLowerCase() === statusStr.toLowerCase());
  return entry ? entry[0] : statusStr;
};

const getStatusLabel = (status) => {
  const code = normalizeStatusCode(status);
  return STATUS_LABELS[code] || (status ?? '—');
};

const getStatusColor = (status) => {
  const code = normalizeStatusCode(status);
  switch (code) {
    case '1':
    case '2':
      return 'bg-yellow-500';
    case '3':
    case '4':
      return 'bg-orange-500';
    case '5':
    case '6':
      return 'bg-blue-500';
    case '7':
      return 'bg-purple-500';
    case '8':
      return 'bg-green-500';
    case '9':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

// Libellés des événements de traçabilité (suivi public)
const EVENT_TYPE_LABELS = {
  CREATION: 'Commande acceptée',
  DEPART: 'Colis expédié',
  RECEPTION: 'Colis en transit',
};
const getEventLabel = (event) => {
  const type = event?.event_type || '';
  return EVENT_TYPE_LABELS[type] || event?.event_type_display || type.replace(/_/g, ' ') || '—';
};

/** Affiche les infos d'un point relais (objet avec nom, adresse, ville) */
const formatRelayDisplay = (relay) => {
  if (!relay || typeof relay !== 'object') return null;
  const nom = relay.nom || relay.name || '—';
  const adresse = relay.adresse || relay.address || '';
  const ville = relay.ville || relay.vilage || '';
  const parts = [nom];
  if (adresse) parts.push(adresse);
  if (ville) parts.push(ville);
  return parts.filter(Boolean).join(' — ');
};

const formatDate = (date, options = {}) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
  } catch (error) {
    return date;
  }
};

const extractAddressInfo = (addressObj) => {
  if (!addressObj) return initialAddress;

  if (typeof addressObj === 'string') {
    return {
      name: addressObj,
      phone: 'Non renseigné',
      email: 'Non renseigné',
      address: addressObj,
      company: ''
    };
  }

  if (typeof addressObj === 'object') {
    return {
      name: `${addressObj.customer_first_name || ''} ${addressObj.customer_last_name || ''}`.trim() ||
        `${addressObj.recipient_first_name || ''} ${addressObj.recipient_last_name || ''}`.trim() ||
        addressObj.name || 'Non renseigné',
      phone: addressObj.customer_phone_number || addressObj.pickup_phone_number || 'Non renseigné',
      email: addressObj.customer_email || addressObj.recipient_email || addressObj.pickup_email || addressObj.email || 'Non renseigné',
      address: addressObj.address || addressObj.full_address || 'Non renseigné',
      company: addressObj.pickup_company_name || addressObj.company_name || ''
    };
  }

  return initialAddress;
};

const Tracking = () => {
  const location = useLocation();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingError, setTrackingError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);

  const performTracking = useCallback(async (rawNumber) => {
    const formattedNumber = (rawNumber || '').trim();
    if (!formattedNumber) {
      setTrackingError('Veuillez saisir un numéro de suivi');
      return;
    }
    setIsLoading(true);
    setTrackingError('');
    setTrackingResult(null);
    try {
      const response = await expeditionAPI.trackExpedition(formattedNumber);
      if (response?.data) {
        setTrackingResult(response.data);
      } else {
        setTrackingError('Aucun colis trouvé avec ce numéro d\'expédition');
      }
    } catch (error) {
      console.error('Erreur lors du suivi:', error);
      if (error.response?.status === 404) {
        setTrackingError(error.response?.data?.error || 'Aucun colis trouvé avec ce numéro d\'expédition');
      } else {
        setTrackingError(error.response?.data?.error || 'Erreur lors de la récupération des informations. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      return true;
    } catch {
      return false;
    }
  };

  const handleTracking = async (e) => {
    e.preventDefault();
    await performTracking(trackingNumber);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let prefilledNumber =
      params.get('tracking') ||
      params.get('numero') ||
      params.get('code') ||
      params.get('trackingnumber') ||
      params.get('reference');

    if (!prefilledNumber) {
      for (const value of params.values()) {
        if (value) {
          prefilledNumber = value;
          break;
        }
      }
    }

    if (prefilledNumber) {
      setTrackingNumber(prefilledNumber);
      performTracking(prefilledNumber);
    }
  }, [location.search, performTracking]);

  return (
    <div className="container-ksl max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Suivi de colis</h1>

      <form onSubmit={handleTracking} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Numéro de suivi
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="Numéro d'expédition (ex: KSL-12345)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ksl-red"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-ksl-red text-white font-medium rounded-lg hover:bg-ksl-red-dark transition-colors duration-200 disabled:opacity-50"
          >
            {isLoading ? 'Recherche...' : 'Suivre'}
          </button>
        </div>
        {trackingError && (
          <p className="mt-3 text-sm text-red-600">{trackingError}</p>
        )}
      </form>

      {trackingResult && (
        <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-w-6xl mx-auto">
          <TrackingTimelineBlock
            data={trackingResult}
            isLoading={isLoading}
            recipientDisplay={
              trackingResult?.recipient?.recipient_name ||
              [trackingResult?.recipient?.recipient_first_name, trackingResult?.recipient?.recipient_last_name].filter(Boolean).join(' ').trim() ||
              '—'
            }
            onCopyTracking={async (num) => {
              const ok = await copyToClipboard(num);
              if (ok) window.alert?.('Numéro copié !');
            }}
          />
        </div>
      )}
    </div>
  );
};
export default Tracking;
